import { ApiContractError, normalizeAdminResponse } from './admin-contract.js';

const API_BASE = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.VITE_ADMIN_API_BASE ?? '/api';

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (!text.trim()) return undefined as T;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Keep compatibility with endpoints that return plain text while avoiding a
    // JSON.parse failure for successful responses with a non-JSON body.
    return text as T;
  }
  return normalizeAdminResponse<T>(parsed as T | import('./admin-contract.js').AdminEnvelope<T>).data;
}

interface RequestBehavior {
  preserveSessionOnUnauthorized?: boolean;
}

function handleUnauthorized(response: Response, behavior: RequestBehavior): void {
  if (
    response.status !== 401
    || behavior.preserveSessionOnUnauthorized
    || typeof window === 'undefined'
  ) return;
  window.localStorage.removeItem('flashmate.admin.token');
  window.localStorage.removeItem('flashmate.admin.user');
  window.location.assign('/login');
}

function requestId(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  return cryptoApi?.randomUUID?.() ?? `admin-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function request<T>(path: string, init: RequestInit, behavior: RequestBehavior = {}): Promise<T> {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('flashmate.admin.token') : null;
  const rid = requestId();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  headers.set('X-Request-Id', rid);
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  handleUnauthorized(response, behavior);
  if (!response.ok) {
    let message = `请求失败（${response.status}）`;
    let code = 'HTTP_ERROR';
    let details: unknown;
    let responseRequestId = response.headers.get('X-Request-Id') ?? rid;
    try {
      const body = await response.json() as { error?: unknown; requestId?: string };
      responseRequestId = body.requestId ?? responseRequestId;
      const error = body.error;
      if (typeof error === 'string') message = error;
      else if (error && typeof error === 'object') {
        const typed = error as { code?: string; message?: string; details?: unknown };
        message = typed.message ?? message;
        code = typed.code ?? code;
        details = typed.details;
      }
    } catch {
      // Non-JSON errors retain the HTTP status message.
    }
    throw new ApiContractError(message, { code, status: response.status, requestId: responseRequestId, details });
  }
  return parseResponse<T>(response);
}

export async function apiGet<T>(path: string, options: Pick<RequestInit, 'signal'> = {}): Promise<T> {
  return request<T>(path, { method: 'GET', ...options });
}

export async function apiMutation<T = unknown>(path: string, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST', body?: unknown, options: Pick<RequestInit, 'signal'> = {}): Promise<T> {
  return request<T>(path, { method, headers: { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body), ...options });
}

export async function apiMutationPreservingSession<T = unknown>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  body?: unknown,
): Promise<T> {
  return request<T>(
    path,
    { method, headers: { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) },
    { preserveSessionOnUnauthorized: true },
  );
}
